import { Fragment, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  IconButton,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DownloadIcon from '@mui/icons-material/Download';
import {
  useApplyFieldCaptureBatch,
  useFieldCaptureEnabled,
  useDiscardFieldCaptureBatch,
  useFieldCaptureBatches,
  useFieldCaptureLinkTable,
  useFieldCaptureQuarantine,
  useLinkFieldCaptureStoreToProject,
  useResolveQuarantinedSubmission,
  useUploadFieldCapturePull,
  useBrightstarExport,
  useFieldCaptureSerialReview,
  useFieldCaptureOpenVisits,
  useFieldCaptureProjects,
  useRegenerateFieldCaptureLinks,
  useResolveFieldCaptureSerial,
  useRunFieldCaptureOcr,
} from '@hooks/api/field-capture';
import {
  FieldCaptureBatchKind,
  FieldCaptureIngestBatch,
  FieldCapturePullScope,
  FieldCaptureSerialReview,
  FieldCaptureStore,
  FieldCaptureValueSource,
} from '@models/fieldCapture.model';
import { useOrganizations } from '@hooks/api/organizations';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { UserRoles } from '@models/user.model';
import { useTranslation } from '@hooks/useTranslation';
import LinkProjectModal from './components/LinkProjectModal';

const extractErrorMessage = (err: any, fallback: string): string =>
  err?.response?.errors?.[0]?.message || err?.message || fallback;

const statusChip = (store: FieldCaptureStore) => {
  if (!store.inScope) return <Chip size="small" color="error" label="DO NOT VISIT" />;
  if (store.status === 'COMPLETED') return <Chip size="small" color="success" label="Submitted" />;
  if (store.status === 'OPENED') return <Chip size="small" color="warning" label="Opened" />;
  return <Chip size="small" label="Pending" />;
};

/**
 * The client's nine terminal states. Colour carries the action:
 * green = accepted, amber = a human should look, red = never adopted,
 * plain = no work is owed by anyone.
 */
const verdictChip = (verdict: string) => {
  switch (verdict) {
    case 'CONFIRMED':
      return <Chip size="small" color="success" label="Confirmed" />;
    case 'CAPTURED':
      return <Chip size="small" color="warning" label="Captured" />;
    case 'ADJUDICATE':
      return <Chip size="small" color="warning" label="Adjudicate" />;
    case 'RETAKE':
      return <Chip size="small" color="error" label="Retake" />;
    case 'WRONG-LABEL':
      return <Chip size="small" color="error" label="Wrong label" />;
    case 'WRONG-SLOT':
      return <Chip size="small" color="error" label="Wrong slot" />;
    case 'DUPLICATE-FLEET':
      return <Chip size="small" color="error" label="Duplicate" />;
    case 'NO-SERIAL-REQUIRED':
      return <Chip size="small" label="No serial owed" />;
    case 'UNMODELED':
      // Our registry gap, not crew error — this must never read as their fault.
      return <Chip size="small" label="No grammar yet" />;
    default:
      return <Chip size="small" label={verdict} />;
  }
};

const batchChip = (batch: FieldCaptureIngestBatch) => {
  if (batch.status === 'APPLIED') return <Chip size="small" color="success" label="Applied" />;
  if (batch.status === 'DISCARDED') return <Chip size="small" label="Discarded" />;
  return <Chip size="small" color="warning" label="Pending" />;
};

/** Escape one CSV cell. */
const csvCell = (value: unknown): string => {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Today as the CREW's day, not the browser's.
 *
 * The export slices report dates in America/Chicago (see REPORT_TIME_ZONE in
 * field-capture-export.service.ts). A reviewer sitting in another timezone who
 * clicks "Today" must get the same day the server will filter on, or the daily
 * cut silently comes back empty near midnight. 'en-CA' formats as YYYY-MM-DD.
 */
const todayLocalIso = (): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

const FieldCaptureAdminPage: React.FC = () => {
  const tActionMessages = useTranslation('actionMessages');
  const [tab, setTab] = useState(0);

  const { role, isReadOnly } = useAppSelector((state: RootState) => state.user);
  const isSuperAdmin = role === UserRoles.SUPER_ADMIN;
  // Reviewers get the full picture but may change nothing — they are here for the
  // Brightstar CSV, and an edit from this seat would corrupt the client deliverable.
  // Read-only org members are held to the same rule.
  const readOnly = isReadOnly || role === UserRoles.REVIEWER;

  // Super-admins belong to no organization — every operation targets an
  // explicitly selected org. Org admins operate on their own org context.
  const [orgId, setOrgId] = useState('');
  // filter must be a valid organization_status_enum value ('ENABLED'/'DISABLED')
  // — the service's default 'Active' is a legacy string the DB enum rejects.
  const { data: orgsData } = useOrganizations(
    isSuperAdmin ? { filter: 'ENABLED', limit: 200 } : { filter: 'ENABLED', limit: 1 },
  );
  const organizations = isSuperAdmin ? (orgsData?.organizations ?? []) : [];
  const { data: atavEnabled } = useFieldCaptureEnabled(!isSuperAdmin);
  const effectiveOrgId = isSuperAdmin ? orgId || undefined : undefined;

  // ── Ingest tab state ──────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadKind, setUploadKind] = useState<FieldCaptureBatchKind>('MASTER');
  // Defaults to PARTIAL: only a declared-complete pull may de-scope the
  // terminals it omits, so the destructive option is always a deliberate choice.
  const [pullScope, setPullScope] = useState<FieldCapturePullScope>('PARTIAL');
  const [pullDate, setPullDate] = useState('');
  // Report-date window for the Brightstar CSV. Scope is explicit rather than two
  // rival download buttons: 'all' ignores the dates entirely, 'dated' uses them,
  // and leaving `to` empty exports the single day in `from` — the daily cut.
  const [brightstarScope, setBrightstarScope] = useState<'all' | 'dated'>('all');
  const [brightstarFrom, setBrightstarFrom] = useState('');
  const [brightstarTo, setBrightstarTo] = useState('');
  const [diffBatch, setDiffBatch] = useState<FieldCaptureIngestBatch | null>(null);
  const ingestActive = isSuperAdmin ? Boolean(orgId) : atavEnabled === true;
  const { data: batches = [], isLoading: batchesLoading } = useFieldCaptureBatches(
    effectiveOrgId,
    ingestActive,
  );
  const uploadMutation = useUploadFieldCapturePull();
  const applyMutation = useApplyFieldCaptureBatch();
  const discardMutation = useDiscardFieldCaptureBatch();

  // ── Links tab state ───────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const linksActive = isSuperAdmin ? Boolean(orgId) : atavEnabled === true;
  const { data: links = [], isLoading: linksLoading } = useFieldCaptureLinkTable(
    search || undefined,
    effectiveOrgId,
    linksActive,
  );
  const linkProjectMutation = useLinkFieldCaptureStoreToProject();
  const { data: openVisits = [] } = useFieldCaptureOpenVisits(effectiveOrgId, linksActive);
  // Shares LinkProjectModal's react-query cache key, so this costs no extra call.
  const { data: projects } = useFieldCaptureProjects(effectiveOrgId, linksActive);
  const regenerateMutation = useRegenerateFieldCaptureLinks();

  const handleRegenerateLinks = async () => {
    try {
      const updated = await regenerateMutation.mutateAsync({ organizationId: effectiveOrgId });
      toast.success(
        updated === 0
          ? tActionMessages.linksAlreadyUpToDate
          : `${updated} link${updated === 1 ? '' : 's'} regenerated with the current prefill set`,
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, tActionMessages.failedToRegenerateLinks));
    }
  };

  // ── Serial review tab state ───────────────────────────────────────────────
  const reviewActive = isSuperAdmin ? Boolean(orgId) : atavEnabled === true;
  const { data: serialReview = [], isLoading: serialReviewLoading } =
    useFieldCaptureSerialReview(effectiveOrgId, reviewActive);
  const brightstarExportMutation = useBrightstarExport();
  const runOcrMutation = useRunFieldCaptureOcr();
  const resolveSerialMutation = useResolveFieldCaptureSerial();
  const [editingSerial, setEditingSerial] = useState<FieldCaptureSerialReview | null>(null);
  const [editedValue, setEditedValue] = useState('');

  // ── Quarantine tab state ──────────────────────────────────────────────────
  const { data: quarantine = [], isLoading: quarantineLoading } = useFieldCaptureQuarantine(isSuperAdmin);
  const resolveMutation = useResolveQuarantinedSubmission();

  // Counts are per TERMINAL — that is the unit of work and of the CSV export.
  const linkStats = useMemo(() => {
    const inScope = links.filter((l) => l.inScope);
    return {
      total: links.length,
      stores: new Set(links.map((l) => l.locationId ?? l.externalKey)).size,
      inScope: inScope.length,
      submitted: inScope.filter((l) => l.status === 'COMPLETED').length,
      opened: inScope.filter((l) => l.status === 'OPENED').length,
      linked: inScope.filter((l) => !!l.projectId).length,
    };
  }, [links]);

  /** Terminals folded into their store, preserving the server's ordering. */
  const storeGroups = useMemo(() => {
    const byLocation = new Map<string, { key: string; head: FieldCaptureStore; terminals: FieldCaptureStore[] }>();
    for (const row of links) {
      const key = row.locationId ?? `__${row.externalKey}`;
      const group = byLocation.get(key);
      if (!group) {
        byLocation.set(key, { key, head: row, terminals: [row] });
        continue;
      }
      group.terminals.push(row);
      // The primary terminal supplies the store row's facts when it turns up.
      if (row.isPrimaryTerminal && !group.head.isPrimaryTerminal) group.head = row;
    }
    return Array.from(byLocation.values());
  }, [links]);

  const [expandedStores, setExpandedStores] = useState<Set<string>>(new Set());
  const toggleStore = (key: string) =>
    setExpandedStores((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const projectNameById = useMemo(
    () => new Map<string, string>((projects ?? []).map((p) => [p.projectId, p.name])),
    [projects],
  );

  /**
   * Which store already holds each project. A project MAY span several stores —
   * that is deliberate — but linking a second one is worth a confirmation, since
   * every terminal of both stores then shares one project feed.
   */
  const storesByProject = useMemo(() => {
    const map = new Map<string, Map<string, string>>();
    for (const row of links) {
      if (!row.projectId) continue;
      const key = row.locationId ?? `__${row.externalKey}`;
      const stores = map.get(row.projectId) ?? new Map<string, string>();
      stores.set(key, row.storeName);
      map.set(row.projectId, stores);
    }
    return map;
  }, [links]);

  const handleUpload = async (file: File) => {
    try {
      const batch = await uploadMutation.mutateAsync({
        file,
        kind: uploadKind,
        sourcePullDate: pullDate || undefined,
        organizationId: effectiveOrgId,
        pullScope,
      });
      toast.success(
        `Parsed ${batch.rowsParsed} rows: +${batch.storesAdded} / ~${batch.storesChanged} / −${batch.storesDropped}` +
          (batch.storesUntouched ? ` (${batch.storesUntouched} untouched)` : '') +
          (batch.rowsQuarantined ? ` (${batch.rowsQuarantined} quarantined)` : ''),
      );
      setDiffBatch(batch);
    } catch (err) {
      toast.error(extractErrorMessage(err, tActionMessages.uploadFailed));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleApply = async (batch: FieldCaptureIngestBatch) => {
    try {
      await applyMutation.mutateAsync({ batchId: batch.fieldCaptureIngestBatchId, organizationId: effectiveOrgId });
      toast.success(tActionMessages.batchApplied);
      setDiffBatch(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, tActionMessages.applyFailed));
    }
  };

  const handleDiscard = async (batch: FieldCaptureIngestBatch) => {
    try {
      await discardMutation.mutateAsync({ batchId: batch.fieldCaptureIngestBatchId, organizationId: effectiveOrgId });
      toast.success(tActionMessages.batchDiscarded);
      setDiffBatch(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, tActionMessages.discardFailed));
    }
  };

  const [linkTarget, setLinkTarget] = useState<FieldCaptureStore | null>(null);

  const handleLinkProject = async (store: FieldCaptureStore, projectId: string | null) => {
    // Linking is store-wide, and a project may legitimately span stores — but the
    // operator should know they are about to merge two stores onto one project
    // feed rather than discover it from the crew's card list.
    if (projectId) {
      const holders = storesByProject.get(projectId);
      const thisKey = store.locationId ?? `__${store.externalKey}`;
      const others = Array.from(holders?.entries() ?? []).filter(([key]) => key !== thisKey);
      if (others.length > 0) {
        const names = others.map(([, name]) => name).join(', ');
        const ok = window.confirm(
          `${projectNameById.get(projectId) ?? 'That project'} is already linked to ${names}.\n\n` +
            'Linking it here as well means both stores share one project feed, and crews opening ' +
            'that project will see every terminal from all of them.\n\nLink anyway?',
        );
        if (!ok) return;
      }
    }
    try {
      await linkProjectMutation.mutateAsync({
        externalKey: store.externalKey,
        projectId,
        organizationId: effectiveOrgId,
      });
      toast.success(projectId ? tActionMessages.projectLinkedToStore : tActionMessages.projectUnlinked);
      setLinkTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, tActionMessages.failedToLinkProject));
    }
  };

  const handleResolve = async (submissionId: string) => {
    const externalKey = window.prompt('Resolve against store master_id (externalKey):');
    if (!externalKey) return;
    try {
      await resolveMutation.mutateAsync({ submissionId, externalKey: externalKey.trim(), organizationId: effectiveOrgId });
      toast.success(tActionMessages.submissionResolvedAndProcessed);
    } catch (err) {
      toast.error(extractErrorMessage(err, tActionMessages.failedToResolveSubmission));
    }
  };

  const handleExportCsv = () => {
    const headers = [
      'externalKey',
      'locationId',
      'retnum',
      'terminalId',
      'currentTermType',
      'terminalPack',
      'terminalIndex',
      'terminalCount',
      'recordStatus',
      'storeName',
      'city',
      'termPacks',
      'commWork',
      'inScope',
      'scopeReason',
      'scheduledDate',
      'status',
      'openedAt',
      'submittedAt',
      'submittedByUserName',
      'projectId',
      'formUrl',
      'sourcePullDate',
      'lastSeenPullDate',
    ];
    const rows = links.map((l) =>
      [
        l.externalKey,
        l.locationId,
        l.retnum,
        l.terminalId,
        l.currentTermType,
        l.terminalPack,
        l.terminalIndex,
        l.terminalCount,
        l.recordStatus,
        l.storeName,
        l.city,
        (l.termPacks || []).join(' '),
        l.commWork ? 'yes' : 'no',
        l.inScope ? 'yes' : 'no',
        l.scopeReason,
        l.scheduledDate,
        l.status,
        l.openedAt,
        l.submittedAt,
        l.submittedByUserName,
        l.projectId,
        l.formUrl,
        l.sourcePullDate,
        l.lastSeenPullDate,
      ]
        .map(csvCell)
        .join(','),
    );
    const blob = new Blob([[headers.join(','), ...rows].join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `field-capture-links-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /**
   * The client deliverable: the comp report in Brightstar's own column layout,
   * built server-side so the header row matches theirs byte-for-byte.
   */
  const handleBrightstarExport = async () => {
    const dated = brightstarScope === 'dated';
    try {
      const result = await brightstarExportMutation.mutateAsync({
        organizationId: effectiveOrgId,
        completedOnly: false,
        // 'All terminals' deliberately sends no window: every terminal, whether
        // or not anyone has reported it yet.
        from: dated ? brightstarFrom || undefined : undefined,
        to: dated ? brightstarTo || brightstarFrom || undefined : undefined,
      });
      const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(
        dated
          ? `Brightstar CSV built — ${result.rowCount} terminal(s) reported in that window`
          : `Brightstar CSV built — ${result.rowCount} terminal(s)`,
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, tActionMessages.exportFailed));
    }
  };

  const handleRunOcr = async () => {
    try {
      const result = await runOcrMutation.mutateAsync({ organizationId: effectiveOrgId });
      if (result.haltedReason) {
        toast.warning(result.haltedReason);
      } else {
        toast.success(
          `OCR: ${result.read} read, ${result.flaggedForReview} flagged, ${result.remainingPending} left`,
        );
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, tActionMessages.ocrRunFailed));
    }
  };

  const handleResolveSerial = async (
    review: FieldCaptureSerialReview,
    value: string,
    source: FieldCaptureValueSource,
  ) => {
    try {
      await resolveSerialMutation.mutateAsync({
        fieldCaptureValueId: review.fieldCaptureValueId,
        value,
        source,
        organizationId: effectiveOrgId,
      });
      setEditingSerial(null);
      toast.success(`${review.headerKey} resolved`);
    } catch (err) {
      toast.error(extractErrorMessage(err, tActionMessages.couldNotResolveSerial));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Field Capture (ATAV / Brightstar)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Daily-pull ingest, pregenerated form links, and the submission quarantine queue.
      </Typography>

      {!isSuperAdmin && atavEnabled === false ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6">ATAV is not enabled for your organization</Typography>
          <Typography variant="body2" color="text.secondary">
            Contact ZenHammer to enroll in the field-capture program.
          </Typography>
        </Paper>
      ) : null}

      {isSuperAdmin ? (
        <Select
          size="small"
          displayEmpty
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          sx={{ mb: 2, minWidth: 320 }}
        >
          <MenuItem value="">Select organization…</MenuItem>
          {organizations.map((org: any) => (
            <MenuItem key={org.organizationId} value={org.organizationId}>
              {org.name}
            </MenuItem>
          ))}
        </Select>
      ) : null}

      {isSuperAdmin || atavEnabled === true ? (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Ingest" />
          <Tab label={`Links (${linkStats.submitted}/${linkStats.inScope} submitted)`} />
          <Tab label={`Serial review (${serialReview.length})`} />
          {isSuperAdmin ? <Tab label={`Quarantine (${quarantine.length})`} /> : null}
        </Tabs>
      ) : null}

      {/* ── Ingest ─────────────────────────────────────────────────────────── */}
      {(isSuperAdmin || atavEnabled === true) && tab === 0 && (
        <>
          {!readOnly && (
            <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Select
                size="small"
                value={uploadKind}
                onChange={(e) => setUploadKind(e.target.value as FieldCaptureBatchKind)}
              >
                <MenuItem value="MASTER">Daily master pull</MenuItem>
                <MenuItem value="TERMPACK_MATRIX">Termpack matrix</MenuItem>
              </Select>
              {uploadKind === 'MASTER' && (
                <Select
                  size="small"
                  value={pullScope}
                  sx={{ minWidth: 340 }}
                  onChange={(e) => setPullScope(e.target.value as FieldCapturePullScope)}
                >
                  <MenuItem value="PARTIAL">Partial / sample file — absent terminals unchanged</MenuItem>
                  <MenuItem value="FULL">Full master pull — absent terminals leave scope</MenuItem>
                </Select>
              )}
              <TextField
                size="small"
                type="date"
                label="Pull date"
                InputLabelProps={{ shrink: true }}
                value={pullDate}
                onChange={(e) => setPullDate(e.target.value)}
              />
              <Button
                variant="contained"
                startIcon={
                  uploadMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />
                }
                disabled={uploadMutation.isPending || (isSuperAdmin && !orgId)}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload CSV
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
              />
              <Typography variant="caption" color="text.secondary">
                XLSX files must be re-exported as CSV first. Nothing changes until a batch is applied.
                {uploadKind === 'MASTER' && pullScope === 'FULL' && (
                  <>
                    {' '}
                    <strong>
                      A full pull marks every terminal missing from this file DO NOT VISIT — only use it for the
                      client&apos;s complete record.
                    </strong>
                  </>
                )}
              </Typography>
            </Paper>
          )}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>File</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell align="right">Rows</TableCell>
                  <TableCell align="right">In scope</TableCell>
                  <TableCell align="right">+ Added</TableCell>
                  <TableCell align="right">~ Changed</TableCell>
                  <TableCell align="right">− Dropped</TableCell>
                  <TableCell align="right">= Untouched</TableCell>
                  <TableCell align="right">! Quarantined</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batchesLoading && (
                  <TableRow>
                    <TableCell colSpan={13}>Loading…</TableCell>
                  </TableRow>
                )}
                {batches.map((batch) => (
                  <TableRow key={batch.fieldCaptureIngestBatchId} hover>
                    <TableCell>{new Date(batch.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{batch.filename}</TableCell>
                    <TableCell>{batch.kind === 'MASTER' ? 'Master' : 'Termpacks'}</TableCell>
                    <TableCell>
                      {batch.kind !== 'MASTER' ? (
                        '—'
                      ) : batch.pullScope === 'FULL' ? (
                        <Chip size="small" color="warning" label="Full" />
                      ) : (
                        <Chip size="small" label="Partial" />
                      )}
                    </TableCell>
                    <TableCell align="right">{batch.rowsParsed}</TableCell>
                    <TableCell align="right">{batch.storesInScope}</TableCell>
                    <TableCell align="right">{batch.storesAdded}</TableCell>
                    <TableCell align="right">{batch.storesChanged}</TableCell>
                    <TableCell align="right">{batch.storesDropped}</TableCell>
                    <TableCell align="right">{batch.storesUntouched}</TableCell>
                    <TableCell align="right">{batch.rowsQuarantined}</TableCell>
                    <TableCell>{batchChip(batch)}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => setDiffBatch(batch)}>
                        Diff
                      </Button>
                      {!readOnly && batch.status === 'PENDING' && (
                        <>
                          <Button
                            size="small"
                            color="primary"
                            disabled={applyMutation.isPending}
                            onClick={() => handleApply(batch)}
                          >
                            Apply
                          </Button>
                          <Button
                            size="small"
                            color="inherit"
                            disabled={discardMutation.isPending}
                            onClick={() => handleDiscard(batch)}
                          >
                            Discard
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ── Links ──────────────────────────────────────────────────────────── */}
      {(isSuperAdmin || atavEnabled === true) && tab === 1 && (
        <>
          <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search store / retnum / master ID / terminal ID / city…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 320 }}
            />
            <Typography variant="body2" color="text.secondary">
              {linkStats.stores} stores · {linkStats.inScope} terminals in scope · {linkStats.linked} linked ·{' '}
              {linkStats.submitted} submitted · {linkStats.opened} opened ·{' '}
              {linkStats.total - linkStats.inScope} do-not-visit
            </Typography>
            <Button startIcon={<DownloadIcon />} onClick={handleExportCsv} disabled={links.length === 0}>
              Export CSV
            </Button>
            {/* Prefill params are materialised per row at ingest, so a newly
                added param reaches nobody until the links are rebuilt. */}
            {!readOnly && (
              <Button
                variant="outlined"
                onClick={handleRegenerateLinks}
                disabled={regenerateMutation.isPending || !linksActive}
              >
                {regenerateMutation.isPending ? 'Regenerating…' : 'Regenerate links'}
              </Button>
            )}
          </Paper>

          {/* The client deliverable gets its own block rather than a fourth
              button in the toolbar above: that row is link housekeeping, this is
              the file Brightstar actually receives. One download button with an
              explicit scope beats two rival buttons where the dates silently
              belonged to only one of them. */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ minWidth: 240 }}>
                <Typography variant="subtitle2">Brightstar deliverable</Typography>
                <Typography variant="caption" color="text.secondary">
                  The comp report in Brightstar&apos;s column layout — one row per terminal.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={brightstarScope}
                  onChange={(_, next: 'all' | 'dated' | null) => next && setBrightstarScope(next)}
                  disabled={!reviewActive}
                >
                  <ToggleButton value="all">All terminals</ToggleButton>
                  <ToggleButton value="dated">By report date</ToggleButton>
                </ToggleButtonGroup>

                {/* Dates appear only when they mean something. Reports are pulled
                    daily, so the common case is one day — hence the shortcut and
                    the optional end date. */}
                {brightstarScope === 'dated' && (
                  <>
                    <Button
                      size="small"
                      onClick={() => {
                        setBrightstarFrom(todayLocalIso());
                        setBrightstarTo('');
                      }}
                      disabled={!reviewActive}
                    >
                      Today
                    </Button>
                    <TextField
                      label="From"
                      type="date"
                      size="small"
                      value={brightstarFrom}
                      onChange={(e) => setBrightstarFrom(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={!reviewActive}
                      sx={{ width: 170 }}
                    />
                    <TextField
                      label="To"
                      type="date"
                      size="small"
                      value={brightstarTo}
                      onChange={(e) => setBrightstarTo(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={!reviewActive}
                      sx={{ width: 170 }}
                    />
                  </>
                )}

                <Button
                  startIcon={<DownloadIcon />}
                  variant="contained"
                  onClick={handleBrightstarExport}
                  disabled={
                    brightstarExportMutation.isPending ||
                    !reviewActive ||
                    (brightstarScope === 'dated' && !brightstarFrom)
                  }
                >
                  {brightstarExportMutation.isPending ? 'Building…' : 'Download CSV'}
                </Button>
              </Box>
            </Box>

            {/* Say what the click will actually produce, so nobody has to infer
                it from which button they pressed. */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {brightstarScope === 'all'
                ? 'Every terminal in the pull, including ones nobody has reported yet — those rows come back blank so the client can upsert them.'
                : !brightstarFrom
                  ? 'Pick a report date to enable the download.'
                  : `Terminals reported ${
                      !brightstarTo || brightstarTo === brightstarFrom
                        ? `on ${brightstarFrom}`
                        : `between ${brightstarFrom} and ${brightstarTo}`
                    }, by the crew's local day (America/Chicago).`}
            </Typography>
          </Paper>

          {/* A crew that drove off before closing out leaves the visit's
              completion status missing. The terminal data is already stored —
              this is what makes the gap chaseable on the day. */}
          {openVisits.length > 0 && (
            <Paper sx={{ p: 2, mb: 2, borderLeft: 4, borderColor: 'warning.main' }}>
              <Typography variant="subtitle2" color="warning.main">
                {openVisits.length} open {openVisits.length === 1 ? 'visit' : 'visits'} — terminals reported,
                store never closed out
              </Typography>
              <Typography variant="caption" color="text.secondary">
                No completion status arrived for these stores. Ask the crew to reopen any terminal and answer
                &ldquo;I&apos;m done here&rdquo;.
              </Typography>
              <Box sx={{ mt: 1 }}>
                {openVisits.map((visit) => (
                  <Typography key={visit.locationId} variant="body2">
                    {visit.storeName} (#{visit.retnum || visit.locationId}) — {visit.terminalsReported} of{' '}
                    {visit.terminalsTotal} terminals reported
                    {visit.lastReportedAt
                      ? ` · last report ${new Date(visit.lastReportedAt).toLocaleString()}`
                      : ''}
                  </Typography>
                ))}
              </Box>
            </Paper>
          )}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 44 }} />
                  <TableCell>Store</TableCell>
                  <TableCell>Retailer #</TableCell>
                  <TableCell>Terminals</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Packs</TableCell>
                  <TableCell>Comm</TableCell>
                  <TableCell>Scheduled</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Project</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {linksLoading && (
                  <TableRow>
                    <TableCell colSpan={11}>Loading…</TableCell>
                  </TableRow>
                )}
                {/* One row per STORE; its terminals expand beneath it. Linking is
                    store-wide, so the project cell belongs on the store row. */}
                {storeGroups.map(({ key, head, terminals }) => {
                  // Most stores have exactly one terminal. There is nothing to
                  // expand there, so the store row simply IS the terminal row —
                  // no chevron, and the real per-terminal status and submission
                  // rather than an aggregate of one.
                  const only = terminals.length === 1 ? terminals[0] : null;
                  const open = !only && expandedStores.has(key);
                  const inScope = terminals.filter((t) => t.inScope);
                  const submitted = inScope.filter((t) => t.status === 'COMPLETED').length;
                  const lastSubmittedAt = terminals
                    .map((t) => t.submittedAt)
                    .filter(Boolean)
                    .sort()
                    .pop();
                  const projectName = head.projectId ? projectNameById.get(head.projectId) : undefined;
                  const sharedWith = head.projectId
                    ? Array.from(storesByProject.get(head.projectId)?.entries() ?? []).filter(([k]) => k !== key)
                    : [];
                  return (
                    <Fragment key={key}>
                      <TableRow hover sx={{ '& > *': { borderBottom: open ? 'unset' : undefined } }}>
                        <TableCell>
                          {only ? null : (
                            <IconButton size="small" onClick={() => toggleStore(key)} aria-label="Show terminals">
                              {open ? (
                                <KeyboardArrowDownIcon fontSize="small" />
                              ) : (
                                <ChevronRightIcon fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>{head.storeName}</TableCell>
                        <TableCell>{head.retnum || head.locationId}</TableCell>
                        <TableCell>
                          {only ? (
                            <>
                              {only.currentTermType || '—'}
                              <Typography variant="caption" color="text.secondary" display="block">
                                master_id {only.externalKey}
                                {only.terminalId ? ` · ${only.terminalId}` : ''}
                              </Typography>
                            </>
                          ) : (
                            <>
                              {terminals.length}
                              <Typography variant="caption" color="text.secondary" display="block">
                                {inScope.length === 0
                                  ? 'none visitable'
                                  : `${submitted} of ${inScope.length} reported`}
                              </Typography>
                            </>
                          )}
                        </TableCell>
                        <TableCell>{head.city}</TableCell>
                        <TableCell>
                          {only ? only.terminalPack || '—' : (head.termPacks || []).join(', ')}
                        </TableCell>
                        <TableCell>{head.commWork ? 'Swap' : 'Reuse'}</TableCell>
                        <TableCell>{head.scheduledDate || '—'}</TableCell>
                        <TableCell>
                          {only ? (
                            statusChip(only)
                          ) : inScope.length === 0 ? (
                            <Chip size="small" color="error" label="DO NOT VISIT" />
                          ) : submitted === inScope.length ? (
                            <Chip size="small" color="success" label="All reported" />
                          ) : submitted > 0 ? (
                            <Chip size="small" color="warning" label="In progress" />
                          ) : (
                            <Chip size="small" label="Pending" />
                          )}
                        </TableCell>
                        <TableCell>
                          {only ? (
                            only.submittedAt ? (
                              `${new Date(only.submittedAt).toLocaleString()}${
                                only.submittedByUserName ? ` · ${only.submittedByUserName}` : ''
                              }`
                            ) : (
                              '—'
                            )
                          ) : lastSubmittedAt ? (
                            <Typography variant="caption" color="text.secondary">
                              last {new Date(lastSubmittedAt).toLocaleString()}
                            </Typography>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          {head.projectId ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="body2">{projectName ?? head.projectId}</Typography>
                              {sharedWith.length > 0 && (
                                <Chip
                                  size="small"
                                  color="warning"
                                  label={`shared with ${sharedWith.map(([, n]) => n).join(', ')}`}
                                />
                              )}
                              {!readOnly && (
                                <>
                                  <Button size="small" onClick={() => setLinkTarget(head)}>
                                    Change
                                  </Button>
                                  <Button
                                    size="small"
                                    color="inherit"
                                    disabled={linkProjectMutation.isPending}
                                    onClick={() => handleLinkProject(head, null)}
                                  >
                                    Unlink
                                  </Button>
                                </>
                              )}
                            </Box>
                          ) : readOnly ? (
                            '—'
                          ) : (
                            <Button size="small" onClick={() => setLinkTarget(head)}>
                              Link…
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      {open &&
                        terminals.map((terminal) => (
                          <TableRow key={terminal.fieldCaptureStoreId} sx={{ bgcolor: 'action.hover' }}>
                            <TableCell />
                            <TableCell>
                              <Typography variant="body2">
                                master_id <strong>{terminal.externalKey}</strong>
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {terminal.terminalId || 'no terminal id'}
                              </Typography>
                            </TableCell>
                            {/* Each terminal has its OWN retailer number: it equals
                                location_id on the first terminal, then location_id
                                + a 2-digit suffix on the rest. Showing only the
                                store row's made every store look like a plain
                                integer and hid the per-terminal values. */}
                            <TableCell>{terminal.retnum || '—'}</TableCell>
                            <TableCell>
                              {terminal.currentTermType || '—'}
                              <Typography variant="caption" color="text.secondary" display="block">
                                {terminal.terminalIndex} of {terminal.terminalCount}
                                {terminal.isPrimaryTerminal ? ' · main' : ''}
                                {terminal.recordStatus ? ` · ${terminal.recordStatus}` : ''}
                              </Typography>
                            </TableCell>
                            <TableCell />
                            <TableCell>{terminal.terminalPack || '—'}</TableCell>
                            <TableCell />
                            <TableCell>{terminal.scheduledDate || '—'}</TableCell>
                            <TableCell>{statusChip(terminal)}</TableCell>
                            <TableCell>
                              {terminal.submittedAt
                                ? `${new Date(terminal.submittedAt).toLocaleString()}${
                                    terminal.submittedByUserName ? ` · ${terminal.submittedByUserName}` : ''
                                  }`
                                : '—'}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        ))}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ── Serial review ──────────────────────────────────────────────────── */}
      {(isSuperAdmin || atavEnabled === true) && tab === 2 && (
        <>
          <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
              Serials where the photo and the typed value disagree, the read was uncertain, or the
              serial does not match the shape expected for that equipment. The photo is the source
              of truth — a confident read already wins the export; resolving here just clears the
              flag.
            </Typography>
            {isSuperAdmin ? (
              <Button
                variant="outlined"
                onClick={handleRunOcr}
                disabled={runOcrMutation.isPending || !reviewActive}
              >
                {runOcrMutation.isPending ? 'Reading…' : 'Run OCR now'}
              </Button>
            ) : null}
          </Paper>

          {serialReviewLoading ? (
            <CircularProgress size={24} />
          ) : serialReview.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Nothing to review — every captured serial is either agreed or confidently read.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Store</TableCell>
                    <TableCell>Column</TableCell>
                    <TableCell>Typed</TableCell>
                    <TableCell>Photo (OCR)</TableCell>
                    <TableCell>Confidence</TableCell>
                    <TableCell>Flags</TableCell>
                    {!readOnly && <TableCell align="right">Resolve</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {serialReview.map((review) => (
                    <TableRow key={review.fieldCaptureValueId} hover>
                      <TableCell>
                        {review.storeName || '—'}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {review.externalKey}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {review.headerKey}
                        {review.serialRule ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {review.serialRule}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {review.typedValue || <em>none</em>}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {review.ocrValue || <em>none</em>}
                        {review.adoptedMethod ? (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
                            via {review.adoptedMethod}
                          </Typography>
                        ) : null}
                        {review.ocrNotes ? (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
                            {review.ocrNotes}
                          </Typography>
                        ) : null}
                        {/* A rule that hides its own mistakes cannot be corrected, so
                            the reviewer sees what was refused and by which pattern. */}
                        {review.rejectedTokens?.length ? (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
                            refused: {review.rejectedTokens.slice(0, 4).join(', ')}
                            {review.rejectedTokens.length > 4 ? ` +${review.rejectedTokens.length - 4} more` : ''}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {typeof review.ocrConfidence === 'number'
                          ? `${Math.round(review.ocrConfidence * 100)}%`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {review.verdict ? verdictChip(review.verdict) : null}
                          {review.mismatch ? <Chip size="small" color="warning" label="Mismatch" /> : null}
                          {review.ocrStatus === 'REFUSED' ? (
                            <Chip size="small" label="OCR refused" />
                          ) : null}
                          {review.ocrStatus === 'FAILED' ? (
                            <Chip size="small" label="OCR failed" />
                          ) : null}
                        </Box>
                      </TableCell>
                      {!readOnly && (
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              disabled={!review.ocrValue || resolveSerialMutation.isPending}
                              onClick={() =>
                                handleResolveSerial(review, review.ocrValue as string, 'OCR')
                              }
                            >
                              Accept photo
                            </Button>
                            <Button
                              size="small"
                              disabled={!review.typedValue || resolveSerialMutation.isPending}
                              onClick={() =>
                                handleResolveSerial(review, review.typedValue as string, 'TYPED')
                              }
                            >
                              Accept typed
                            </Button>
                            <Button
                              size="small"
                              onClick={() => {
                                setEditingSerial(review);
                                setEditedValue(review.ocrValue || review.typedValue || '');
                              }}
                            >
                              Edit
                            </Button>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ── Quarantine ─────────────────────────────────────────────────────── */}
      {isSuperAdmin && tab === 3 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Received</TableCell>
                <TableCell>Submission ID</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quarantineLoading && (
                <TableRow>
                  <TableCell colSpan={4}>Loading…</TableCell>
                </TableRow>
              )}
              {!quarantineLoading && quarantine.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>Queue is empty — every submission resolved.</TableCell>
                </TableRow>
              )}
              {quarantine.map((submission) => (
                <TableRow key={submission.fieldCaptureSubmissionId} hover>
                  <TableCell>{new Date(submission.receivedAt).toLocaleString()}</TableCell>
                  <TableCell>{submission.tallySubmissionId}</TableCell>
                  <TableCell>{submission.quarantineReason}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      disabled={resolveMutation.isPending}
                      onClick={() => handleResolve(submission.fieldCaptureSubmissionId)}
                    >
                      Resolve to store…
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {linkTarget ? (
        <LinkProjectModal
          store={linkTarget}
          organizationId={effectiveOrgId}
          saving={linkProjectMutation.isPending}
          onClose={() => setLinkTarget(null)}
          onLink={handleLinkProject}
        />
      ) : null}

      {/* ── Manual serial correction ───────────────────────────────────────── */}
      <Dialog open={!!editingSerial} onClose={() => setEditingSerial(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editingSerial?.headerKey} — {editingSerial?.storeName}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Typed: <code>{editingSerial?.typedValue || '—'}</code>
            {' · '}
            Photo: <code>{editingSerial?.ocrValue || '—'}</code>
            {editingSerial?.serialRule ? (
              <>
                <br />
                {editingSerial.serialRule}
              </>
            ) : null}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Serial"
            value={editedValue}
            onChange={(e) => setEditedValue(e.target.value)}
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingSerial(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!editedValue.trim() || resolveSerialMutation.isPending}
            onClick={() =>
              editingSerial && handleResolveSerial(editingSerial, editedValue.trim(), 'TYPED')
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Diff dialog ────────────────────────────────────────────────────── */}
      <Dialog open={!!diffBatch} onClose={() => setDiffBatch(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Diff — {diffBatch?.filename} ({diffBatch?.sourcePullDate})
        </DialogTitle>
        <DialogContent dividers>
          {diffBatch?.kind === 'MASTER' && (
            <Typography variant="body2" sx={{ mb: 2 }} color={diffBatch.pullScope === 'FULL' ? 'warning.main' : undefined}>
              {diffBatch.pullScope === 'FULL'
                ? 'Full pull — terminals listed under “dropped” will be marked DO NOT VISIT on apply.'
                : 'Partial pull — terminals absent from this file are listed as “untouched” and will not change.'}
            </Typography>
          )}
          {(['added', 'changed', 'dropped', 'untouched', 'quarantined'] as const).map((section) => {
            const entries = (diffBatch?.diff?.[section] as any[]) || [];
            if (entries.length === 0) return null;
            return (
              <Box key={section} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                  {section} ({entries.length})
                  {section === 'dropped' && ' — will be marked DO NOT VISIT'}
                  {section === 'untouched' && ' — absent from this file, left as they are'}
                </Typography>
                {entries.slice(0, 100).map((entry: any, i: number) => (
                  <Typography key={i} variant="body2" color="text.secondary">
                    {section === 'quarantined'
                      ? `Row ${entry.row}: ${entry.reason}`
                      : `${entry.storeName} (${entry.externalKey})` +
                        (entry.fields ? ` — ${entry.fields.join(', ')}` : '') +
                        (entry.reason ? ` — ${entry.reason}` : '') +
                        (entry.lastSeenPullDate ? ` — last seen ${entry.lastSeenPullDate}` : '')}
                  </Typography>
                ))}
                {entries.length > 100 && (
                  <Typography variant="caption">…and {entries.length - 100} more</Typography>
                )}
              </Box>
            );
          })}
          {diffBatch &&
            !diffBatch.diff?.added?.length &&
            !diffBatch.diff?.changed?.length &&
            !diffBatch.diff?.dropped?.length &&
            !diffBatch.diff?.untouched?.length &&
            !diffBatch.diff?.quarantined?.length && (
              <Typography variant="body2">No changes — this pull matches the current table.</Typography>
            )}
        </DialogContent>
        <DialogActions>
          {!readOnly && diffBatch?.status === 'PENDING' && (
            <>
              <Button color="inherit" onClick={() => diffBatch && handleDiscard(diffBatch)}>
                Discard
              </Button>
              <Button variant="contained" onClick={() => diffBatch && handleApply(diffBatch)}>
                Apply
              </Button>
            </>
          )}
          <Button onClick={() => setDiffBatch(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FieldCaptureAdminPage;
